// Patch 008.6 → 008.7 — section nav, now rendered INLINE inside the
// header's centered slot rather than as a standalone sticky bar. Project
// lifecycle order: Setup | Today | Plan | Build | Money | People |
// Profiles | Marketing | Ops. Active = gold underline. Mobile (≤720px):
// the inline row hides via CSS and the parent Header surfaces a
// mobile-only <select> that lists the same options.
//
// Chain-fix 2026-05-30 — re-surfaced the project setup wizard (/setup),
// which had become reachable by URL only (no nav entry).

import { useNavigate } from 'react-router-dom'

const OAKRIDGE_ID = '00000000-0a17-0000-0000-000000000001'

export const SECTIONS = [
  { id: 'setup', label: 'Setup' },
  { id: 'today', label: 'Today' },
  { id: 'plan', label: 'Plan' },
  { id: 'build', label: 'Build' },
  { id: 'money', label: 'Money' },
  { id: 'people', label: 'People' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'ops', label: 'Ops' },
] as const

export type SectionId = (typeof SECTIONS)[number]['id']

// Patch 010.E.4 — `profiles` is a cross-project top-level tab; it lives at
// `/profiles` (no projectId prefix) per A.5 walk Batch 1 Q1 resolution.
// All other sections are project-scoped.
export function urlForSection(section: SectionId, projectId: string | undefined): string {
  if (section === 'profiles') return '/profiles'
  const target = projectId ?? OAKRIDGE_ID
  // Patch 015.3 — the Plan section lands on Scope (the subtractive
  // cascade), not the demoted Command Board.
  if (section === 'plan') return `/projects/${target}/plan/scope`
  return `/projects/${target}/${section}`
}

export function Nav(props: {
  projectId?: string
  activeSection: SectionId | null
}): JSX.Element {
  const navigate = useNavigate()
  return (
    <nav className="hcc-header__nav" data-testid="primary-nav">
      {SECTIONS.map((s) => (
        <button
          key={s.id}
          type="button"
          className={`hcc-header__nav-btn${props.activeSection === s.id ? ' active' : ''}`}
          onClick={() => navigate(urlForSection(s.id, props.projectId))}
          data-testid={`nav-${s.id}`}
        >
          {s.label}
        </button>
      ))}
    </nav>
  )
}

export function NavMobile(props: {
  projectId?: string
  activeSection: SectionId | null
}): JSX.Element {
  const navigate = useNavigate()
  return (
    <div className="hcc-header__nav-mobile">
      <select
        className="hcc-header__nav-mobile-select"
        value={props.activeSection ?? 'today'}
        onChange={(e) => navigate(urlForSection(e.target.value as SectionId, props.projectId))}
        aria-label="Section"
        data-testid="nav-mobile-select"
      >
        {SECTIONS.map((s) => (
          <option key={s.id} value={s.id}>
            {s.label}
          </option>
        ))}
      </select>
    </div>
  )
}
